import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Estimate, FacilityLevelOfCare, LevelOfCare, PayorPlan, RateAsPayorMap } from '@finpay-estimation/estimation-types';
import { loadSelectedEstimate, saveSelectedEstimate, createSelectedEstimate, loadSavedEstimatesListv3, getRateAsPayorMap } from './estimator-thunk';

export interface EstimatorState {
  selectedEstimate: Estimate | null;
  savedEstimatesList: Estimate[];
  isLoading: {
    selectedEstimate: boolean;
    savedEstimatesList: boolean;
    rateAsPayorMap: boolean;
  };
  error: {
    selectedEstimate: string | null;
    savedEstimatesList: string | null;
    rateAsPayorMap: string | null;
  };
  rateAsPayorPlan: PayorPlan | null;
  rateAsPayorMap: Record<number, RateAsPayorMap[]> | null;
  facilityLevelsOfCareList: FacilityLevelOfCare[] | null;
}

const initialState: EstimatorState = {
  selectedEstimate: null,
  savedEstimatesList: [],
  isLoading: {
    selectedEstimate: false,
    savedEstimatesList: false,
    rateAsPayorMap: false,
  },
  error: {
    selectedEstimate: null,
    savedEstimatesList: null,
    rateAsPayorMap: null,
  },
  rateAsPayorPlan: null,
  rateAsPayorMap: null,
  facilityLevelsOfCareList: [],
};

export const estimatorSlice = createSlice({
  name: 'estimatorContext',
  initialState,
  reducers: {
    setSelectedEstimate: (state, action: PayloadAction<Estimate | null>) => {
      state.selectedEstimate = action.payload;
      state.error.selectedEstimate = null;
    },
    clearSelectedEstimate: (state) => {
      state.selectedEstimate = null;
      state.error.selectedEstimate = null;
    },
    setSelectedEstimateLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading.selectedEstimate = action.payload;
    },
    setSelectedEstimateError: (state, action: PayloadAction<string | null>) => {
      state.error.selectedEstimate = action.payload;
      state.isLoading.selectedEstimate = false;
    },
    clearSelectedEstimateError: (state) => {
      state.error.selectedEstimate = null;
    },
    setSavedEstimatesList: (state, action: PayloadAction<Estimate[]>) => {
      state.savedEstimatesList = action.payload;
      state.error.savedEstimatesList = null;
    },
    clearSavedEstimatesList: (state) => {
      state.savedEstimatesList = [];
      state.error.savedEstimatesList = null;
    },
    setSavedEstimatesListLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading.savedEstimatesList = action.payload;
    },
    setSavedEstimatesListError: (state, action: PayloadAction<string | null>) => {
      state.error.savedEstimatesList = action.payload;
      state.isLoading.savedEstimatesList = false;
    },
    clearSavedEstimatesListError: (state) => {
      state.error.savedEstimatesList = null;
    },
    setRateAsPayorPlan: (state, action) => {
      state.rateAsPayorPlan = action.payload;
    },
    setFacilityLevelsOfCareList: (state, action) => {
      state.facilityLevelsOfCareList = action.payload;
    },
    setRateAsPayorMap: (state, action: PayloadAction<RateAsPayorMap[]>) => {
      // Group the rate as payor map data by clientId
      const rateAsPayorMapByClientId: Record<number, RateAsPayorMap[]> = {};

      action.payload.forEach(item => {
        if (item.clientId !== null && item.clientId !== undefined) {
          const clientIdKey = Number(item.clientId);
          if (!rateAsPayorMapByClientId[clientIdKey]) {
            rateAsPayorMapByClientId[clientIdKey] = [];
          }
          rateAsPayorMapByClientId[clientIdKey].push(item);
        }
      });

      state.rateAsPayorMap = rateAsPayorMapByClientId;
      state.error.rateAsPayorMap = null;
    },
    setRateAsPayorMapLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading.rateAsPayorMap = action.payload;
    },
    setRateAsPayorMapError: (state, action: PayloadAction<string | null>) => {
      state.error.rateAsPayorMap = action.payload;
      state.isLoading.rateAsPayorMap = false;
    },
    clearRateAsPayorMap: (state) => {
      state.rateAsPayorMap = null;
      state.error.rateAsPayorMap = null;
    },
  },
  extraReducers: (builder) => {
    // Load selected estimate
    builder.addCase(loadSelectedEstimate.pending, (state) => {
      state.isLoading.selectedEstimate = true;
      state.error.selectedEstimate = null;
    });
    builder.addCase(loadSelectedEstimate.fulfilled, (state, action) => {
      state.isLoading.selectedEstimate = false;
      state.selectedEstimate = action.payload;
      state.error.selectedEstimate = null;
    });
    builder.addCase(loadSelectedEstimate.rejected, (state, action) => {
      state.isLoading.selectedEstimate = false;
      state.error.selectedEstimate = action.error.message || 'Failed to load estimate';
    });

    // Save selected estimate
    builder.addCase(saveSelectedEstimate.pending, (state) => {
      state.isLoading.selectedEstimate = true;
      state.error.selectedEstimate = null;
    });
    builder.addCase(saveSelectedEstimate.fulfilled, (state, action) => {
      state.isLoading.selectedEstimate = false;
      state.selectedEstimate = action.payload;
      state.error.selectedEstimate = null;
      // Update the corresponding estimate in the saved estimates list
      const updatedEstimate = action.payload;
      if (updatedEstimate) {
        const existingIndex = state.savedEstimatesList.findIndex(est => est.estimateId === updatedEstimate.estimateId);
        if (existingIndex !== -1) {
          state.savedEstimatesList[existingIndex] = updatedEstimate;
        }
      }
    });
    builder.addCase(saveSelectedEstimate.rejected, (state, action) => {
      state.isLoading.selectedEstimate = false;
      state.error.selectedEstimate = action.error.message || 'Failed to save estimate';
    });

    // Create selected estimate
    builder.addCase(createSelectedEstimate.pending, (state) => {
      state.isLoading.selectedEstimate = true;
      state.error.selectedEstimate = null;
    });
    builder.addCase(createSelectedEstimate.fulfilled, (state, action) => {
      state.isLoading.selectedEstimate = false;
      state.selectedEstimate = action.payload;
      state.error.selectedEstimate = null;
      // Adding  new estimate to the saved estimates list if it doesn't exist
      const newEstimate = action.payload;
      if (newEstimate && !state.savedEstimatesList.some(est => est.estimateId === newEstimate.estimateId)) {
        state.savedEstimatesList.push(newEstimate);
      }
    });
    builder.addCase(createSelectedEstimate.rejected, (state, action) => {
      state.isLoading.selectedEstimate = false;
      state.error.selectedEstimate = action.error.message || 'Failed to create estimate';
    });

    // Load saved estimates list
    builder.addCase(loadSavedEstimatesListv3.pending, (state) => {
      state.isLoading.savedEstimatesList = true;
      state.error.savedEstimatesList = null;
    });
    builder.addCase(loadSavedEstimatesListv3.fulfilled, (state, action) => {
      state.isLoading.savedEstimatesList = false;
      state.savedEstimatesList = action.payload;
      state.error.savedEstimatesList = null;
    });
    builder.addCase(loadSavedEstimatesListv3.rejected, (state, action) => {
      state.isLoading.savedEstimatesList = false;
      state.error.savedEstimatesList = action.error.message || 'Failed to load saved estimates';
    });

    // Get rate as payor map
    builder.addCase(getRateAsPayorMap.pending, (state) => {
      state.isLoading.rateAsPayorMap = true;
      state.error.rateAsPayorMap = null;
    });
    builder.addCase(getRateAsPayorMap.fulfilled, (state, action) => {
      state.isLoading.rateAsPayorMap = false;

      // Group the rate as payor map data by clientId
      const rateAsPayorMapByClientId: Record<number, RateAsPayorMap[]> = {};

      action.payload.forEach(item => {
        if (item.clientId !== null && item.clientId !== undefined) {
          const clientIdKey = Number(item.clientId);
          if (!rateAsPayorMapByClientId[clientIdKey]) {
            rateAsPayorMapByClientId[clientIdKey] = [];
          }
          rateAsPayorMapByClientId[clientIdKey].push(item);
        }
      });

      state.rateAsPayorMap = rateAsPayorMapByClientId;
      state.error.rateAsPayorMap = null;
    });
    builder.addCase(getRateAsPayorMap.rejected, (state, action) => {
      state.isLoading.rateAsPayorMap = false;
      state.error.rateAsPayorMap = action.error.message || 'Failed to load rate as payor map';
    });
  },
});

export const {
  setSelectedEstimate,
  clearSelectedEstimate,
  setSelectedEstimateLoading,
  setSelectedEstimateError,
  clearSelectedEstimateError,
  setSavedEstimatesList,
  clearSavedEstimatesList,
  setSavedEstimatesListLoading,
  setSavedEstimatesListError,
  clearSavedEstimatesListError,
  setRateAsPayorPlan,
  setRateAsPayorMap,
  setRateAsPayorMapLoading,
  setRateAsPayorMapError,
  clearRateAsPayorMap
} = estimatorSlice.actions;

export default estimatorSlice.reducer; 